// src/pages/settings/PrinterSettings.jsx
// Printer assignment per job type — local installs only (LOCAL_MODE=true)
import React, { useState, useEffect, useCallback } from 'react';
import api, { isLocalInstall } from '../../config/api';

const JOB_TYPES = [
  { key: 'receipt_80mm', label: '80mm Receipt (POS)' },
  { key: 'invoice_a4',   label: 'A4 Invoice / Document' },
  { key: 'kitchen',      label: 'Kitchen Ticket' },
  { key: 'report',       label: 'Reports' },
];

export default function PrinterSettings() {
  const [printers,  setPrinters]  = useState([]);
  const [assigned,  setAssigned]  = useState({}); // { receipt_80mm: 'EPSON TM-T20', ... }
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState(null);
  const [saved,     setSaved]     = useState(false);

  // ── Load printer list + current settings ──────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [printersRes, settingsRes] = await Promise.all([
        api.get('/api/printers'),
        api.get('/api/settings'),
      ]);
      setPrinters(printersRes.data.printers || []);
      setAssigned(settingsRes.data.printers || {});
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Save ──────────────────────────────────────────────────────────────────
  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await api.put('/api/settings', { printers: assigned });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Test print ─────────────────────────────────────────────────────────────
  const testPrint = async (jobType) => {
    const label = JOB_TYPES.find(j => j.key === jobType)?.label || jobType;
    const html = `
      <html><body style="font-family:monospace;padding:20px">
        <h2>🖨 Test Print</h2>
        <p>Job type: <strong>${label}</strong></p>
        <p>Printer: <strong>${assigned[jobType] || '(none)'}</strong></p>
        <p>${new Date().toLocaleString()}</p>
      </body></html>`;
    try {
      await api.post('/api/print', { html, job_type: jobType });
      alert('Test sent to printer!');
    } catch (e) {
      const msg = e.response?.data?.error;
      if (msg === 'no_printer_configured') {
        alert('No printer configured for this job type yet.');
      } else {
        alert('Print error: ' + (msg || e.message));
      }
    }
  };

  if (!isLocalInstall) {
    return (
      <div className="p-6 text-gray-500 text-sm">
        Printer settings are only available on local (USB) installs.
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl">
      <h2 className="text-lg font-semibold text-gray-800 mb-1">Printer Assignment</h2>
      <p className="text-sm text-gray-500 mb-5">
        Assign a printer to each document type. Printing will happen silently — no dialog will appear.
      </p>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          {error}
          <button onClick={load} className="ml-3 underline text-red-600">Retry</button>
        </div>
      )}

      {/* Saved */}
      {saved && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-700">
          ✓ Printer settings saved.
        </div>
      )}

      {loading ? (
        <div className="text-sm text-gray-400 py-8 text-center">Loading printers…</div>
      ) : (
        <>
          {/* Table */}
          <div className="border rounded-lg overflow-hidden mb-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Document Type</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Printer</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {JOB_TYPES.map((job, i) => (
                  <tr key={job.key} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-3 font-medium text-gray-700">{job.label}</td>
                    <td className="px-4 py-3">
                      <select
                        className="w-full border rounded px-2 py-1 text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#2e9f86]"
                        value={assigned[job.key] || ''}
                        onChange={e => setAssigned(prev => ({ ...prev, [job.key]: e.target.value }))}
                      >
                        <option value="">— Not configured —</option>
                        {printers.map(p => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => testPrint(job.key)}
                        className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-100"
                        title="Send test page to this printer"
                      >
                        Test
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Refresh + Save */}
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
            >
              ↺ Refresh Printers
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="px-5 py-2 text-sm rounded text-white font-semibold disabled:opacity-60"
              style={{ backgroundColor: '#2e9f86' }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>

          {/* Hint */}
          {printers.length === 0 && (
            <p className="mt-4 text-xs text-amber-600">
              No printers found. Make sure at least one printer is installed in Windows (Settings → Printers &amp; scanners), then click Refresh Printers.
            </p>
          )}
        </>
      )}
    </div>
  );
}
